import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAutoAwesome } from 'react-icons/md';
import client from '../api/client';
import { messaggioErrore } from '../utils/apiError';
import { Button } from './ui';

// «Cosa vorresti ottenere?» (idea dell'utente, 30/09/2026): si dice un
// desiderio e l'assistente risponde se con i servizi che ci sono si può, cosa
// userebbe, e porta alla regola da creare o a cercare il servizio che manca.
// Non crea niente da solo.
const ESITI = {
  si: { testo: 'Sì, si può', tono: 'border-brand-500 text-brand-700' },
  in_parte: { testo: 'In parte', tono: 'border-warning text-warning' },
  no: { testo: 'Non con quello che c’è', tono: 'border-line text-muted' },
};
const ESEMPI = ['Vorrei sapere la mattina se il volo di Claudia è in ritardo',
  'Vorrei la formazione migliore per la prossima giornata del fanta',
  'Vorrei ricordarmi di comprare il pane quando esco dal lavoro'];

export default function CosaVorrei({ onCercaServizio }) {
  const navigate = useNavigate();
  const [desiderio, setDesiderio] = useState('');
  const [esito, setEsito] = useState(null);
  const [risposta, setRisposta] = useState(null);
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState('');
  // «Voli domani per Atene da Roma?» (30/09/2026): chi scrive una domanda vuole
  // la risposta, non un parere su cosa si potrebbe fare. «Rispondimi adesso» la
  // chiede all'assistente; «Posso automatizzarlo?» porta a una regola.
  const rispondi = async e => {
    e.preventDefault();
    setInCorso('risposta'); setErrore(''); setEsito(null); setRisposta(null);
    try {
      const { data } = await client.post('/v1/servizi/chiedi/', { domanda: desiderio }, { timeout: 90000 });
      setRisposta(data);
    } catch (err) { setErrore(messaggioErrore(err, 'Non sono riuscito a rispondere.')); }
    finally { setInCorso(''); }
  };
  const chiedi = async () => {
    setInCorso('valuta'); setErrore(''); setEsito(null); setRisposta(null);
    try {
      const { data } = await client.post('/v1/servizi/cosa-vorrei/', { desiderio }, { timeout: 60000 });
      setEsito(data);
    } catch (err) { setErrore(messaggioErrore(err, 'Non sono riuscito a rispondere.')); }
    finally { setInCorso(''); }
  };
  const tono = esito && ESITI[esito.esito];
  return <section className="space-y-3 rounded-xl border bg-surface p-4">
    <h2 className="flex items-center gap-2 font-semibold"><MdAutoAwesome aria-hidden className="text-brand-600" /> Cosa vorresti ottenere?</h2>
    <p className="text-sm text-muted">Scrivilo come lo diresti. «Rispondimi adesso» te lo cerca con i servizi che hai;
      «Posso automatizzarlo?» ti dice se ne può nascere un comando o un avviso, e cosa manca.</p>
    <form className="space-y-2" onSubmit={rispondi}>
      <textarea className="w-full rounded-lg border bg-surface px-3 py-2 text-sm" rows={2} aria-label="Cosa vorresti ottenere"
        placeholder="Es. vorrei sapere la mattina se il volo di Claudia è in ritardo" value={desiderio}
        onChange={e => setDesiderio(e.target.value)} />
      <div className="flex flex-wrap gap-1">
        {ESEMPI.map(esempio => <button key={esempio} type="button" onClick={() => setDesiderio(esempio)}
          className="rounded-full border px-2 py-1 text-xs text-muted hover:bg-canvas">{esempio}</button>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" loading={inCorso === 'risposta'} disabled={!desiderio.trim() || !!inCorso}>Rispondimi adesso</Button>
        <Button loading={inCorso === 'valuta'} disabled={!desiderio.trim() || !!inCorso} onClick={chiedi}>Posso automatizzarlo?</Button>
      </div>
    </form>
    {risposta && <div role="status" aria-label="Risposta" className="space-y-2 rounded-lg bg-surface-2 p-3 text-sm">
      <p className="whitespace-pre-wrap text-ink">{risposta.risposta || 'Nessuna risposta.'}</p>
      <details className="text-xs text-muted"><summary className="cursor-pointer">Come ho risposto</summary>
        <p className="mt-1 whitespace-pre-wrap">{risposta.dettagli}</p></details>
      <p className="text-xs text-muted">{risposta.avviso}</p>
    </div>}
    {errore && <p role="alert" className="text-sm text-warning">{errore}</p>}
    {esito && <div role="status" aria-label="Risposta dell’assistente" className="space-y-2 rounded-lg bg-surface-2 p-3 text-sm">
      <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${tono.tono}`}>{tono.testo}</span>
      <p className="whitespace-pre-wrap text-ink">{esito.risposta}</p>
      {esito.strumenti.length > 0 && <p className="text-xs text-muted">Userei: {esito.strumenti.map(s => s.nome).join(', ')}</p>}
      <div className="flex flex-wrap gap-2">
        {esito.regola && <Button variant="primary" onClick={() => navigate('/automations/nuova', { state: { frase: esito.regola } })}>
          Crea la regola</Button>}
        {esito.servizio_da_cercare && <Button onClick={() => onCercaServizio(esito.servizio_da_cercare)}>Cerca un servizio adatto</Button>}
      </div>
      {esito.regola && <p className="text-xs text-muted">La regola parte da: «{esito.regola}». La controlli e la provi prima di accenderla.</p>}
    </div>}
    {/* Per chi preferisce non pensare ai comandi: uno solo, e l'assistente sceglie. */}
    <p className="text-sm text-muted">Vuoi un comando solo per tutto?
      <button type="button" className="ml-1 text-brand-700 underline" onClick={() => navigate('/automations', { state: { modello: 'assistente-comando' } })}>
        Crea l’assistente @w</button></p>
  </section>;
}
